import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Select, Button, message, Table, Spin, Card, Row, Col } from "antd";
import api from "../services/api";
import moment from "moment";

const { Option } = Select;

const MatchDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matchTeams, setMatchTeams] = useState([]);
  const [innings, setInnings] = useState([]);
  const [ballsByInning, setBallsByInning] = useState({});
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const fetchMatch = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/matches/${id}`);
      setMatch(res.data.data);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch match details");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const res = await api.get("/teams");
      setTeams(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMatchTeams = async () => {
    try {
      const res = await api.get(`/matchteams/${id}`, { params: { match_id: id } });
      setMatchTeams(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchInningsAndBalls = async () => {
    try {
      const res = await api.get(`/innings`, { params: { match_id: id } });
      const inningsData = res.data.data || [];
      setInnings(inningsData);

      // Fetch balls for each inning
      const ballsMap = {};
      await Promise.all(
        inningsData.map(async (inn) => {
          const ballsRes = await api.get(`/balls/inning/${inn.inning_id}`);
          ballsMap[inn.inning_id] = ballsRes.data.data || [];
        })
      );
      setBallsByInning(ballsMap);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchMatch();
    fetchTeams();
    fetchMatchTeams();
    fetchInningsAndBalls();
  }, [id]);

  const handleAddTeam = async (values) => {
    try {
      const payload = { match_id: id, team_id: values.team_id, role: values.role };
      const res = await api.post("/matchteams", payload);

      if (res.data.success) {
        message.success("Team added to match");
        const newTeam = {
          id: res.data.data.id,
          match_id: id,
          team_id: values.team_id,
          role: values.role,
          Team: teams.find((t) => t.team_id === values.team_id),
        };
        setMatchTeams((prev) => [...prev, newTeam]);
        form.resetFields();
      } else {
        message.error(res.data.message || "Failed to add team");
      }
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || "Failed to add team");
    }
  };

  const handleStartMatch = () => {
    if (matchTeams.length < 2) {
      message.warning("At least 2 teams required to start match");
      return;
    }
    navigate(`/dashboard/matches/${id}/live`);
  };

  const getInningTotals = (inning) => {
  const balls = ballsByInning[inning.inning_id] || [];
  const runs = balls.reduce((sum, b) => sum + (b.runs || 0) + (b.extras || 0), 0);
  const wickets = balls.filter((b) => b.wicket).length;
  return { runs, wickets, balls };
};

const getTeamName = (teamId) => {
  const team = matchTeams.find((t) => t.team_id === teamId) || teams.find((t) => t.team_id === teamId);
  return team?.Team?.name || team?.name || `Team ${teamId}`;
};


const getMatchResult = () => {
  if (innings.length === 0) return "-";

  const firstInning = getInningTotals(innings[0]);
  const secondInning = innings[1] ? getInningTotals(innings[1]) : null;

  if (!secondInning) return "-"; // only first inning played

  const firstTeamName = getTeamName(innings[0].batting_team_id);
  const secondTeamName = getTeamName(innings[1].batting_team_id);

  if (secondInning.runs > firstInning.runs) {
    return `${secondTeamName} won by ${11 - secondInning.wickets} wickets`;
  } else if (secondInning.runs < firstInning.runs) {
    const runDiff = firstInning.runs - secondInning.runs;
    return `${firstTeamName} won by ${runDiff} runs`;
  } else {
    return "Match Draw!";
  }
};




  const teamColumns = [
    { title: "Team", key: "Teamname", render: (text, record) => record.Team?.name || "-" },
    { title: "Role", dataIndex: "role", key: "role" },
  ];

  const ballColumns = [
    { title: "Over", dataIndex: "over", key: "over" },
    { title: "Ball", dataIndex: "ball_number", key: "ball_number" },
    { title: "Runs", dataIndex: "runs", key: "runs" },
    { title: "Extras", dataIndex: "extras", key: "extras" },
    { title: "Batsman", key: "batsman", render: (_, record) => record.batsman?.name || record.batsman_id },
    { title: "Non-Striker", key: "nonStriker", render: (_, record) => record.non_striker?.name || record.non_striker_id },
    { title: "Bowler", key: "bowler", render: (_, record) => record.bowler?.name || record.bowler_id },
    { title: "Wicket", dataIndex: "wicket", key: "wicket", render: (w) => (w ? "Yes" : "No") },
  ];

  return (
    <div>
      {loading || !match ? (
        <Spin size="large" />
      ) : (
        <>
          <Card title={`Match: ${match.name}`} style={{ marginBottom: 20 }}>
            <Row gutter={16}>
              <Col span={8}><strong>Tournament:</strong> {match.tournament_name}</Col>
              <Col span={8}><strong>Status:</strong> {match.status}</Col>
              <Col span={8}><strong>Venue:</strong> {match.venue || "N/A"}</Col>
              <Col span={8}><strong>Start:</strong> {moment(match.start_time).format("DD MMM, hh:mm A")}</Col>
              <Col span={8}><strong>End:</strong> {match.end_time ? moment(match.end_time).format("DD MMM, hh:mm A") : "-"}</Col>
              <Col span={8}><strong>Format:</strong> {match.overs}-Overs ({match.match_type})</Col>
              <Col span={8}><strong>Result:</strong> {getMatchResult()}</Col>
            </Row>

            {match.status === "live" && (
              <Button type="primary" style={{ marginTop: 10 }} onClick={handleStartMatch}>
                Start Match
              </Button>
            )}
          </Card>


          <Card title="Teams in Match" style={{ marginBottom: 20 }}>
            <Table columns={teamColumns} dataSource={matchTeams} rowKey="id" pagination={false} />
          </Card>

          {innings.length > 0 && (
  <Card title="Innings Overview">
    {innings.map((inning, idx) => {
      const balls = ballsByInning[inning.inning_id] || [];

      // Compute total runs
      const totalRuns = balls.reduce((sum, b) => sum + (b.runs || 0) + (b.extras || 0), 0);

      // Compute total wickets
      const totalWickets = balls.filter((b) => b.wicket).length;

      return (
        <Card
          type="inner"
          title={`Inning ${idx + 1} - ${inning.batting_team?.name || "Team"} ${
            idx === 1 && innings[0] ? `(Target: ${innings[0].runs + 1 || totalRuns + 1})` : ""
          }`}
          key={inning.inning_id}
          style={{ marginBottom: 10 }}
        >
          <Row gutter={16}>
            <Col span={6}><strong>Batting:</strong> {inning.batting_team?.name || "-"}</Col>
            <Col span={6}><strong>Bowling:</strong> {inning.bowling_team?.name || "-"}</Col>
            <Col span={6}><strong>Runs:</strong> {totalRuns}</Col>
            <Col span={6}><strong>Wickets:</strong> {totalWickets}</Col>
            <Col span={6}><strong>Overs:</strong> {balls.length > 0 ? `${Math.floor(balls.length / 6)}.${balls.length % 6}` : 0}</Col>
          </Row>

          <Table
            columns={ballColumns}
            dataSource={balls}
            rowKey={(record) => record.ball_id || record.id}
            pagination={false}
            style={{ marginTop: 10 }}
          />
        </Card>
      );
    })}
  </Card>
)}

          <Card title="Add Team" style={{ marginTop: 20 }}>
            <Form form={form} layout="inline" onFinish={handleAddTeam}>
              <Form.Item name="team_id" rules={[{ required: true, message: "Select a team" }]}>
                <Select placeholder="Select Team" style={{ width: 200 }}>
                  {teams.map((t) => {
                    const isAdded = matchTeams.some((mt) => mt.team_id === t.team_id);
                    return (
                      <Option key={t.team_id} value={t.team_id} disabled={isAdded}>
                        {t.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>

              <Form.Item name="role" rules={[{ required: true, message: "Select role" }]}>
                <Select placeholder="Role" style={{ width: 150 }}>
                  <Option value="home">Home</Option>
                  <Option value="away">Away</Option>
                  <Option value="team1">Team 1</Option>
                  <Option value="team2">Team 2</Option>
                </Select>
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit">Add Team</Button>
              </Form.Item>
            </Form>
          </Card>
        </>
      )}
    </div>
  );
};

export default MatchDetails;
